import { useState } from 'react';

import { API_BASE_URL } from '../config/config';
import {
  ApiResponse_guid,
  ApiResponse_memberDTO,
  ApiResponse_memberRegisterResponseDTO,
  ApiResponse_membershipStatusDTO,
  ApiResponse_searchResponseDTO_1,
  ApiResponse_string,
  MemberDTO,
  MemberRegisterDTO,
  SearchRequestDTO_memberSearchRequestDTO,
  MemberClient,
} from '../services/apiClient';
import axiosInstance from '../services/axiosInstance';
import { handleHookError } from '../utils/hookUtils/handleHookError';

interface UseMemberApi {
  register: (
    data: MemberRegisterDTO,
  ) => Promise<ApiResponse_memberRegisterResponseDTO>;
  edit: (id: string, data: MemberDTO) => Promise<ApiResponse_memberDTO>;
  getById: (id: string) => Promise<ApiResponse_memberDTO>;
  search: (
    filters: SearchRequestDTO_memberSearchRequestDTO,
  ) => Promise<ApiResponse_searchResponseDTO_1>;
  remove: (id: string) => Promise<void>;
  createBillingPortalSession: () => Promise<ApiResponse_string>;
  getMyMembershipStatus: () => Promise<ApiResponse_membershipStatusDTO>;
  resumeMembership: () => Promise<ApiResponse_guid>;
  isLoading: boolean;
  error: string | null;
}

export const useMemberApi = (): UseMemberApi => {
  const apiClient = new MemberClient(API_BASE_URL, axiosInstance);

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Public self sign-up. Members are only ever created this way - there is no admin "create a
  // member" path, since a member is inseparable from the login (ApplicationUser) that self-signup
  // creates alongside it. Admin creation was removed rather than fixed, see MemberService.CreateAsync.
  const register = async (
    data: MemberRegisterDTO,
  ): Promise<ApiResponse_memberRegisterResponseDTO> => {
    setIsLoading(true);
    try {
      return await apiClient.registerMember(data);
    } catch (err: unknown) {
      handleHookError(err, 'Error registering Member', setError);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const edit = async (
    id: string,
    data: MemberDTO,
  ): Promise<ApiResponse_memberDTO> => {
    setIsLoading(true);
    try {
      return await apiClient.updateMember(id, data);
    } catch (err: unknown) {
      handleHookError(err, 'Error updating Member', setError);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const getById = async (id: string): Promise<ApiResponse_memberDTO> => {
    setIsLoading(true);
    try {
      return await apiClient.getMemberById(id);
    } catch (err: unknown) {
      handleHookError(err, 'Error fetching Member', setError);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const search = async (
    filters: SearchRequestDTO_memberSearchRequestDTO,
  ): Promise<ApiResponse_searchResponseDTO_1> => {
    setIsLoading(true);
    try {
      return await apiClient.searchMembers(filters);
    } catch (err: unknown) {
      handleHookError(err, 'Error searching Members', setError);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const remove = async (id: string): Promise<void> => {
    setIsLoading(true);
    try {
      return await apiClient.deleteMember(id);
    } catch (err: unknown) {
      handleHookError(err, 'Error deleting Member', setError);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  // The current member's own "Manage Membership" action - opens Stripe's hosted Customer Portal
  // to update payment details or cancel. No id passed: the backend resolves the member from the
  // JWT.
  const createBillingPortalSession = async (): Promise<ApiResponse_string> => {
    setIsLoading(true);
    try {
      return await apiClient.createMyBillingPortalSession();
    } catch (err: unknown) {
      handleHookError(err, 'Error opening billing portal', setError);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  // Whether the current member has an active (paid-up) membership. An unpaid member is held to
  // the free courses-played limit (isCoursesPlayedCapped), nothing more.
  const getMyMembershipStatus =
    async (): Promise<ApiResponse_membershipStatusDTO> => {
      setIsLoading(true);
      try {
        return await apiClient.getMyMembershipStatus();
      } catch (err: unknown) {
        handleHookError(err, 'Error fetching membership status', setError);
        throw err;
      } finally {
        setIsLoading(false);
      }
    };

  // Gets (creating if needed) the current member's outstanding invoice id for their membership
  // type. Feed the id straight into
  // useInvoicePaymentSummary/PaymentSummary, the same components the emailed "Pay Now" link uses.
  const resumeMembership = async (): Promise<ApiResponse_guid> => {
    setIsLoading(true);
    try {
      return await apiClient.resumeMembership();
    } catch (err: unknown) {
      handleHookError(err, 'Error resuming membership', setError);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  return {
    register,
    edit,
    getById,
    search,
    remove,
    createBillingPortalSession,
    getMyMembershipStatus,
    resumeMembership,
    isLoading,
    error,
  };
};
